import React, { useEffect, useState, useCallback } from "react";
import {
  Autosuggest,
  BreadcrumbGroup,
  Header,
  Input,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Table,
  Box,
} from "@cloudscape-design/components";
import { Link } from "react-router-dom";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface SqsQueue {
  queueUrl: string;
  queueName: string;
  arn: string;
  approximateNumberOfMessages: number;
  approximateNumberOfMessagesNotVisible: number;
  approximateNumberOfMessagesDelayed: number;
  createdTimestamp: string;
  visibilityTimeout: number;
  maximumMessageSize: number;
  messageRetentionPeriod: number;
  fifoQueue: boolean;
}

interface AccountState {
  queues: SqsQueue[];
  loading: boolean;
  error: string | null;
}

export default function LiveSqsPage() {
  useDocumentTitle("SQS");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ label: string; value: string }[]>([]);
  const [region, setRegion] = useState(getDefaultRegion());
  const [filters, setFilters] = useState({ name: "" });
  const [accountStates, setAccountStates] = useState<Record<string, AccountState>>({});

  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
      if (data.length > 0) {
        setSelectedOptions([{ label: `${data[0].Alias} (${data[0].accountid})`, value: data[0].documentId }]);
      }
    })();
  }, []);

  const selectedAccounts = accounts.filter((a) =>
    selectedOptions.some((o) => o.value === a.documentId)
  );

  const allQueues = selectedAccounts.flatMap((a) => accountStates[a.documentId]?.queues || []);

  const fetchQueues = useCallback(
    async (account: Account) => {
      const id = account.documentId;
      setAccountStates((prev) => ({ ...prev, [id]: { queues: [], loading: true, error: null } }));

      const creds = getAwsCreds(account.Alias);
      if (!creds) {
        setAccountStates((prev) => ({ ...prev, [id]: { queues: [], loading: false, error: "AWS credentials missing for this account." } }));
        return;
      }

      try {
        const res = await apiFetch(`/inventory/live/sqs?accountId=${id}&region=${region}`, {
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAccountStates((prev) => ({ ...prev, [id]: { queues: data.queues, loading: false, error: null } }));
      } catch (e: any) {
        setAccountStates((prev) => ({ ...prev, [id]: { queues: [], loading: false, error: e.message } }));
      }
    },
    [region]
  );

  useEffect(() => {
    selectedAccounts.forEach((account) => fetchQueues(account));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, region]);

  const filteredQueues = (queues: SqsQueue[]) =>
    queues.filter(
      (q) => !filters.name || q.queueName.toLowerCase().includes(filters.name.toLowerCase())
    );

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "SQS", href: "/live-inventory/sqs" },
        ]}
      />

      <div className="filter-bar">
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={({ detail }) => setSelectedOptions(detail.selectedOptions as any)}
          options={accounts.map((a) => ({ label: `${a.Alias} (${a.accountid})`, value: a.documentId }))}
          placeholder="Select accounts"
          filteringType="auto"
        />
        <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="Region" />
        <Autosuggest
          value={filters.name}
          onChange={({ detail }) => setFilters((f) => ({ ...f, name: detail.value }))}
          options={Array.from(new Set(allQueues.map((q) => q.queueName))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Name"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
      </div>

      {selectedAccounts.map((account) => {
        const st = accountStates[account.documentId] || { queues: [], loading: false, error: null };

        if (st.error) {
          return (
            <Box key={account.documentId}>
              <Header>{account.Alias} ({account.accountid})</Header>
              <StatusIndicator type="error">{st.error}</StatusIndicator>
            </Box>
          );
        }

        return (
          <Table
            key={account.documentId}
            header={<Header>{account.Alias} ({account.accountid})</Header>}
            columnDefinitions={[
              {
                id: "queueName",
                header: "Queue Name",
                cell: (q: SqsQueue) => (
                  <Link to={`/live-inventory/sqs/${encodeURIComponent(q.queueName)}?account=${account.documentId}&region=${region}`}>
                    {q.queueName}
                  </Link>
                ),
                sortingField: "queueName",
              },
              { id: "messagesAvailable", header: "Messages Available", cell: (q: SqsQueue) => q.approximateNumberOfMessages, sortingField: "approximateNumberOfMessages" },
              { id: "messagesInFlight", header: "Messages In Flight", cell: (q: SqsQueue) => q.approximateNumberOfMessagesNotVisible, sortingField: "approximateNumberOfMessagesNotVisible" },
              { id: "messagesDelayed", header: "Messages Delayed", cell: (q: SqsQueue) => q.approximateNumberOfMessagesDelayed, sortingField: "approximateNumberOfMessagesDelayed" },
              { id: "fifo", header: "FIFO", cell: (q: SqsQueue) => q.fifoQueue ? "Yes" : "No", sortingField: "fifoQueue" },
              { id: "visibilityTimeout", header: "Visibility Timeout", cell: (q: SqsQueue) => q.visibilityTimeout, sortingField: "visibilityTimeout" },
              { id: "created", header: "Created", cell: (q: SqsQueue) => q.createdTimestamp, sortingField: "createdTimestamp" },
            ]}
            items={filteredQueues(st.queues)}
            loading={st.loading}
            sortingDisabled={false}
            resizableColumns
            wrapLines
            stickyHeader
            empty="No SQS queues found."
          />
        );
      })}
    </SpaceBetween>
  );
}
